import {useState} from "react";
import {LANGUAGE_CHOSEN_KEY, saveUiSettings} from "@view/theme/api/uiSettings";
import {getCookie} from "@helpers/cookie/cookie";
import {IAuthenticateBody, ITokensData} from "@containers/auth/types/auth";
import {authenticate, authenticateDemo} from "@containers/auth/api/api";
import {useAuthContext} from "@containers/auth/context/hooks/useAuthContext";
import {useAppContext} from "@context/appContext/hooks/useAppContext";
import {useNavigate} from "react-router-dom";
import {IS_SEPARATED, ROOT_PATH} from "@constants/config";
import {leaveLoginPage, saveTokens} from "@containers/auth/helpers/helpers";
import {buildAuthoritiesMap} from "@containers/auth/helpers/buildAuthoritiesMap";

/** The language picked on the sign in page becomes the user's, once they are in; at most a short wait. */
const keepChosenLanguage = (): Promise<void> => {
    if (!sessionStorage.getItem(LANGUAGE_CHOSEN_KEY)) {
        return Promise.resolve()
    }
    sessionStorage.removeItem(LANGUAGE_CHOSEN_KEY)
    const language = getCookie('language')
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, 2000))
    return Promise.race([saveUiSettings({language}), timeout])
}

export function useAuth(): {
    isLoading: boolean,
    // Label key of what went wrong, shown in the form.
    errorLabel: string | null,
    signIn: (body: IAuthenticateBody) => void,
    signInDemo: () => void,
} {

    const {setIsAuth} = useAuthContext()
    const {
        setUser,
        setAccount,
        setAuthorities,
    } = useAppContext()

    const [isLoading, setIsLoading] = useState(false)
    const [errorLabel, setErrorLabel] = useState<string | null>(null)
    const navigate = useNavigate();

    const completeSignIn = (res: ITokensData) => {
        saveTokens(res)
        if (!IS_SEPARATED) {
            // Keeps the spinner up while the browser leaves the page. The language picked here is kept
            // first: track1 applies the user's kept language when the session starts.
            keepChosenLanguage().finally(leaveLoginPage)
            return
        }
        keepChosenLanguage()
        setUser((prev) => {
            return {
                ...(prev ?? {}),
                active: res.active,
                id: res?.userId,
                name: res?.userName,
                contactName: res?.contactName,
                region: res?.region,
                timeZone: res?.userTimeZone,
                multiAccount: res?.userFromMultiAccount,
                superTenantAccount: res?.userFromSuperTenantAccount,
                tenantAccount: res?.userFromTenantAccount,
            }
        })
        setAccount({
            id: res?.accountId,
            code: res?.accountCode,
            hasSingleVoyage: res.userFromSV || false
        })
        const authoritiesMap = buildAuthoritiesMap(res?.authorities)
        setAuthorities(authoritiesMap)
        setIsAuth(true)
        setIsLoading(false)
        navigate(ROOT_PATH);
    }

    const run = async (request: () => Promise<ITokensData>) => {
        try {
            setIsLoading(true)
            setErrorLabel(null)
            const res = await request()
            if (res?.token) {
                completeSignIn(res)
                return
            }
            setIsLoading(false)
        } catch (error) {
            setIsLoading(false)
            // track-api answers 401 to a wrong password as well as to a disabled user or account.
            setErrorLabel(error?.status === 401 ? 'auth.errorCredentials' : 'auth.errorUnavailable')
        }
    }

    return {
        signIn: (body: IAuthenticateBody) => run(() => authenticate(body)),
        signInDemo: () => run(authenticateDemo),
        isLoading,
        errorLabel,
    }
}
